import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TableBody,
  TableHead,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ValidationError } from '../../../hooks/useFileUpload';
import { useErrorTable } from '../../../hooks/useErrorTable';
import {
  TableWrapper,
  StyledTableContainer,
  StyledTable,
  StyledTableCell,
  StyledTableRow,
  StyledHeaderCell,
} from './ErrorTable.styled';

interface ErrorTableProps {
  errors: ValidationError[];
  fileName?: string;
  maxWidth?: string;
}

interface ErrorColumn {
  Header: string;
  accessor: string;
  width?: string | number;
  Cell?: (props: { value: any }) => React.ReactNode;
}

export const ErrorTable: React.FC<ErrorTableProps> = ({ 
  errors, 
  fileName,
  maxWidth = '100%' 
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Use the custom error table hook without pagination
  const {
    processedErrors,
    totalErrorCount,
    sortErrors
  } = useErrorTable(errors);

  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [activeColumn, setActiveColumn] = useState<string | null>(null);
  const [currentSort, setCurrentSort] = useState<{ column: string; direction: 'ASC' | 'DESC' } | null>(null);

  // Define table columns
  const columns: ErrorColumn[] = useMemo(() => [
    {
      Header: 'Sample ID',
      accessor: 'sampleId',
      width: '120px',
    },
    {
      Header: 'Version',
      accessor: 'version',
      width: '100px',
    },
    {
      Header: 'Field',
      accessor: 'field',
      width: '120px',
    },
    {
      Header: 'Error Code',
      accessor: 'errorCode',
      width: '120px',
    },
    {
      Header: 'Message',
      accessor: 'message',
      width: 'auto',
      Cell: ({ value }: { value: string }) => (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            lineHeight: 1.4,
            padding: '8px 0',
            height: 'auto',
            width: '100%',
            textAlign: 'center',
            fontSize: isMobile ? '11px' : '12px',
          }}
        >
          {value}
        </Typography>
      ),
    }
  ], [isMobile]);

  const handleSortClick = (event: React.MouseEvent<HTMLElement>, column: string) => {
    setActiveColumn(column);
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortSelect = (direction: 'ASC' | 'DESC') => {
    if (activeColumn) {
      setCurrentSort({ column: activeColumn, direction });
      sortErrors(activeColumn, direction);
    }
    setSortAnchorEl(null);
  };

  // Early return if there are no valid errors
  if (totalErrorCount === 0) {
    return null;
  }

  return (
    <TableWrapper maxWidth={maxWidth} data-testid="error-table-wrapper">
      <Box>
        <Typography
          sx={{
            fontSize: '1.375rem',
            textTransform: 'uppercase',
            fontWeight: 700,
            lineHeight: '1.56',
            color: 'rgb(7, 49, 66)',
            fontFamily: "'Lato', sans-serif",
            [theme.breakpoints.down('sm')]: {
              fontSize: '1.25rem',
            },
          }}
          data-testid="error-table-title"
        >
          {`Validation Errors for file "${fileName}" (${totalErrorCount})`}
        </Typography>
      </Box>

      <StyledTableContainer
        sx={{
          maxHeight: '600px',
          overflowY: 'auto',
          overflowX: 'auto',
          mt: 2,
        }}
        data-testid="error-table-container"
      >
        <StyledTable data-testid="error-table">
          <TableHead data-testid="error-table-head">
            <StyledTableRow data-testid="error-table-header-row">
              {columns.map((column: ErrorColumn) => (
                <StyledHeaderCell
                  key={column.accessor}
                  width={column.width || 'auto'}
                  data-testid={`error-header-cell-${column.accessor}`}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      position: 'relative',
                      pr: isMobile ? 0.5 : 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? '10px' : '12px',
                        fontWeight: 700,
                        width: '100%',
                        textAlign: 'center',
                        fontFamily: "'Lato', sans-serif",
                      }}
                    >
                      {column.Header}
                    </Typography>
                    <IconButton
                      size='small'
                      onClick={(e) => handleSortClick(e, column.accessor)}
                      sx={{
                        position: 'absolute',
                        right: -4,
                        top: '50%',
                        backgroundColor: '#E7EFF5 !important',
                        transform: 'translateY(-50%)',
                        padding: isMobile ? '1px' : '2px',
                      }}
                      data-testid={`error-sort-button-${column.accessor}`}
                    >
                      {currentSort && currentSort.column === column.accessor ? (
                        currentSort.direction === 'ASC' ? (
                          <ArrowUpwardIcon sx={{ fontSize: isMobile ? 10 : 12 }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: isMobile ? 10 : 12 }} />
                        )
                      ) : (
                        <MoreVertIcon sx={{ fontSize: isMobile ? 10 : 12 }} />
                      )}
                    </IconButton>
                  </Box>
                </StyledHeaderCell>
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {processedErrors.length === 0 ? (
              <StyledTableRow data-testid="error-table-no-data">
                <StyledTableCell colSpan={columns.length}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 4,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: 'italic' }}
                    >
                      {t('table.noErrors', 'No errors available')}
                    </Typography>
                  </Box>
                </StyledTableCell>
              </StyledTableRow>
            ) : (
              processedErrors.map((row: any, index: number) => (
                <StyledTableRow
                  key={index}
                  className={index % 2 === 1 ? 'alternate' : ''}
                  data-testid={`error-table-row-${index}`}
                  sx={{
                    '& td:last-child': {
                      textAlign: 'left', // Left align message column
                    },
                  }}
                >
                  {columns.map((column: ErrorColumn) => (
                    <StyledTableCell
                      key={column.accessor}
                      width={column.width || 'auto'}
                      data-testid={`error-row-cell-${index}-${column.accessor}`}
                      sx={{
                        // Special styling for message column
                        ...(column.accessor === 'message' && {
                          whiteSpace: 'normal !important',
                          alignItems: 'flex-start !important',
                          minHeight: '60px',
                          height: 'auto !important',
                          maxHeight: 'none',
                          overflow: 'visible !important',
                          textOverflow: 'unset !important',
                          verticalAlign: 'top',
                        }),
                      }}
                    >
                      {column.Cell 
                        ? column.Cell({ value: row[column.accessor] }) 
                        : (
                          <Typography
                            component="div"
                            sx={{
                              fontSize: isMobile ? '11px' : '12px',
                              textAlign: column.accessor === 'message' ? 'left' : 'center',
                              fontFamily: "'Lato', sans-serif",
                            }}
                          >
                            {row[column.accessor] || '-'}
                          </Typography>
                        )
                      }
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>

      {/* Sort menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={() => setSortAnchorEl(null)}
        PaperProps={{
          style: {
            minWidth: '180px',
          },
        }}
        data-testid="error-sort-menu"
      >
        <MenuItem onClick={() => handleSortSelect('ASC')} data-testid="error-sort-ascending">
          <ArrowUpwardIcon sx={{ mr: 1, fontSize: 16 }} />
          {t('table.sortAscending', 'Sort Ascending')}
        </MenuItem>
        <MenuItem onClick={() => handleSortSelect('DESC')} data-testid="error-sort-descending">
          <ArrowDownwardIcon sx={{ mr: 1, fontSize: 16 }} />
          {t('table.sortDescending', 'Sort Descending')}
        </MenuItem>
      </Menu>
    </TableWrapper>
  );
};

export default ErrorTable; 